/**
 * @file Page editor, versions, workflow, publish — RBAC-aware.
 */
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Link, useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import MuiButton from "@mui/material/Button";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { pagePreviewHref, pagesListHref } from "@/lib/pages-navigation";
import { canManageContent, canReviewApprovals, isAdmin } from "@/lib/permissions";
import { PageStatusBadge } from "@/components/PageStatusBadge";
import { VersionCompareDialog } from "@/components/versions/VersionCompareDialog";
import { ApprovalPanel } from "@/components/workflow/ApprovalPanel";
import { pageImmutablePrefix, parsePageSlug, slugify } from "@/lib/shared";

const RichTextEditor = lazy(() =>
  import("@/components/editor/RichTextEditor").then((m) => ({ default: m.RichTextEditor })),
);
const editorFallback = (
  <div className="rounded border border-zinc-200 bg-zinc-50 px-4 py-8 text-center text-sm text-zinc-600">
    Loading editor…
  </div>
);

interface PageDetail {
  id: string;
  title: string;
  slug: string;
  slugAutoFromTitle: boolean;
  status: string;
  publishedVersionId: string | null;
  chapter: { id: string; title: string; guide: { id: string; title: string } };
  currentVersion: { id: string; title: string; body: string; versionNumber: number } | null;
}

interface VersionSummary {
  id: string;
  versionNumber: number;
  title: string;
  source: string;
  createdAt: string;
  createdBy: string;
}

const LIVE_PAGE_STATUSES = new Set(["published", "published_draft", "published_in_review"]);

export function PageDetailPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = useMemo(() => canManageContent(user?.roles), [user?.roles]);
  const canReview = useMemo(() => canReviewApprovals(user?.roles), [user?.roles]);
  const canDelete = useMemo(() => isAdmin(user?.roles), [user?.roles]);

  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id as string;
  const listChapterId = searchParams.get("chapterId");

  const [page, setPage] = useState<PageDetail | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState<{ title: string; body: string } | null>(null);
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [comparePair, setComparePair] = useState<{ v1: string; v2: string } | null>(null);
  const [history, setHistory] = useState<
    Array<{
      id: string;
      status: string;
      currentLevel: number;
      workflow: { name: string | null };
      submittedVersion: { versionNumber: number; title: string };
      actions: Array<{
        id: string;
        action: string;
        level: number;
        comment: string | null;
        createdAt: string;
        actor: string;
      }>;
    }>
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [reviewComment, setReviewComment] = useState("");
  const [editableSlug, setEditableSlug] = useState("");

  const load = useCallback(async () => {
    const [p, v, h] = await Promise.all([
      apiFetch<PageDetail>(`/api/v1/pages/${id}`),
      apiFetch<VersionSummary[]>(`/api/v1/pages/${id}/versions`),
      apiFetch<typeof history>(`/api/v1/pages/${id}/approval-history`),
    ]);
    setPage(p);
    setTitle(p.title);
    const nextBody = p.currentVersion?.body ?? "";
    setBody(nextBody);
    setSavedSnapshot({ title: p.title, body: nextBody });
    setVersions(v);
    setHistory(h);
  }, [id]);

  useEffect(() => {
    void load().catch((e) => setError(String(e)));
  }, [load]);

  useEffect(() => {
    if (!page) {
      return;
    }
    setEditableSlug(parsePageSlug(page.id, page.slug).editable);
  }, [page]);

  async function saveSlugSegment() {
    if (!page) {
      return;
    }
    const current = slugify(parsePageSlug(page.id, page.slug).editable);
    if (slugify(editableSlug) === current) {
      return;
    }
    setError(null);
    try {
      await apiFetch(`/api/v1/pages/${id}`, {
        method: "PATCH",
        json: { slugSuffix: slugify(editableSlug) },
      });
      await load();
    } catch (e) {
      setError(String(e));
    }
  }

  const dirty = useMemo(() => {
    if (!savedSnapshot) {
      return false;
    }
    return title !== savedSnapshot.title || body !== savedSnapshot.body;
  }, [title, body, savedSnapshot]);

  const pending = useMemo(
    () => history.find((r) => r.status === "pending") ?? null,
    [history]
  );

  const canSubmitForReview = useMemo(() => {
    if (!page) {
      return false;
    }
    return ["draft", "published_draft", "unpublished", "rejected"].includes(page.status);
  }, [page]);

  const showPublish = page?.status === "approved";
  const showUnpublish = Boolean(page?.publishedVersionId);

  async function save(manual: boolean) {
    setError(null);
    await apiFetch(`/api/v1/pages/${id}/versions`, {
      method: "POST",
      json: {
        title,
        body,
        source: manual ? "manual" : "autosave",
      },
    });
    await load();
  }

  async function submitReview() {
    setError(null);
    await apiFetch(`/api/v1/pages/${id}/submit-review`, { method: "POST", json: {} });
    await load();
  }

  async function publish() {
    setError(null);
    await apiFetch(`/api/v1/pages/${id}/publish`, { method: "POST" });
    await load();
  }

  async function unpublish() {
    setError(null);
    await apiFetch(`/api/v1/pages/${id}/unpublish`, { method: "POST" });
    await load();
  }

  async function confirmDeletePage() {
    setConfirmDeleteOpen(false);
    setError(null);
    try {
      await apiFetch(`/api/v1/pages/${id}`, { method: "DELETE" });
      navigate(pagesListHref(listChapterId));
    } catch (e) {
      setError(String(e));
    }
  }

  const deletePageMessage = page
    ? LIVE_PAGE_STATUSES.has(page.status) || page.publishedVersionId
      ? `Delete page “${page.title}”? It is or was live on the help site. It will be removed from the CMS and help site.`
      : `Delete page “${page.title}”? It will be removed from the CMS and help site.`
    : "";

  const publishedFromList = useMemo(() => {
    if (!page?.publishedVersionId) {
      return null;
    }
    return versions.find((v) => v.id === page.publishedVersionId) ?? null;
  }, [page?.publishedVersionId, versions]);

  if (!page) {
    return <div className="text-zinc-500">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={pagesListHref(listChapterId)} className="text-sm text-sky-600 hover:underline">
            ← All pages
          </Link>
          <h1 className="mt-2 text-2xl font-semibold">Edit page</h1>
          <div className="mt-3 max-w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
            <p className="truncate whitespace-nowrap">
              <span className="font-medium text-zinc-900">Current version</span>{" "}
              {page.currentVersion ? (
                <span className="text-zinc-600">v{page.currentVersion.versionNumber}</span>
              ) : (
                <span className="text-zinc-500">—</span>
              )}
              {page.publishedVersionId ? (
                <>
                  <span className="text-zinc-300" aria-hidden>
                    {" "}
                    ·{" "}
                  </span>
                  <span className="font-medium text-zinc-900">Published version</span>{" "}
                  {publishedFromList ? (
                    <span className="text-zinc-600">v{publishedFromList.versionNumber}</span>
                  ) : (
                    <span className="text-zinc-500">Live</span>
                  )}
                </>
              ) : null}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={pagePreviewHref(page.id, listChapterId)}
            className="rounded border border-zinc-300 px-3 py-1.5 text-sm text-zinc-800 hover:bg-zinc-50"
          >
            Preview
          </Link>
          <PageStatusBadge status={page.status} />
          {canDelete ? (
            <Tooltip title="Delete page">
              <IconButton
                color="error"
                size="small"
                onClick={() => setConfirmDeleteOpen(true)}
                aria-label="Delete page"
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>
      {error ? <p className="text-sm text-rose-600">{error}</p> : null}

      {!canEdit ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          You don’t have permission to edit this page. You can still preview content.
        </p>
      ) : null}

      <label className="block text-sm">
        <span className="text-zinc-600">Title</span>
        <input
          className="mt-1 w-full rounded border border-zinc-300 px-3 py-2 disabled:bg-zinc-100"
          value={title}
          onChange={(e) => {
            const t = e.target.value;
            setTitle(t);
            if (page.slugAutoFromTitle) {
              setEditableSlug(slugify(t));
            }
          }}
          disabled={!canEdit}
        />
      </label>

      <div className="text-sm">
        <span className="text-zinc-600">URL slug</span>
        <div className="mt-1 flex max-w-full flex-wrap items-center gap-1 rounded border border-zinc-300 bg-zinc-50 px-2 py-1.5">
          <span className="shrink-0 font-mono text-xs text-zinc-500" title="Fixed id prefix (cannot change)">
            {pageImmutablePrefix(page.id)}-
          </span>
          <input
            className="min-w-[8rem] flex-1 border-0 bg-transparent py-0.5 font-mono text-sm outline-none disabled:bg-transparent"
            value={editableSlug}
            onChange={(e) => setEditableSlug(e.target.value.replace(/\s+/g, "-").toLowerCase())}
            onBlur={() => void saveSlugSegment()}
            disabled={!canEdit}
            aria-label="Editable URL slug segment"
          />
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          {page.slugAutoFromTitle
            ? "The editable part follows the title until you change it or save a custom segment (blur field to save)."
            : "Slug is manual — change the segment above and blur to save. Title changes no longer change the URL."}
        </p>
      </div>

      <Suspense fallback={editorFallback}>
        <RichTextEditor content={body} onChange={setBody} editable={canEdit} />
      </Suspense>

      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!dirty}
            className="rounded bg-zinc-900 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => void save(true)}
          >
            Save version
          </button>
          {canSubmitForReview ? (
            <button
              type="button"
              className="rounded border border-zinc-300 px-4 py-2 text-sm"
              onClick={() => void submitReview().catch((e) => setError(String(e)))}
            >
              Submit for review
            </button>
          ) : null}
          {showPublish ? (
            <button
              type="button"
              className="rounded border border-emerald-600 px-4 py-2 text-sm text-emerald-800"
              onClick={() => void publish().catch((e) => setError(String(e)))}
            >
              Publish
            </button>
          ) : null}
          {showUnpublish ? (
            <button
              type="button"
              className="rounded border border-amber-700 px-4 py-2 text-sm text-amber-900"
              onClick={() => void unpublish().catch((e) => setError(String(e)))}
            >
              Unpublish
            </button>
          ) : null}
        </div>
      ) : null}

      <section>
        <h2 className="mb-2 text-lg font-medium">Versions</h2>
        {versions.length === 0 ? (
          <p className="text-sm text-zinc-500">No versions yet.</p>
        ) : (
          <div className="space-y-2">
            {versions.length < 2 ? (
              <p className="text-sm text-zinc-600">
                Save at least two versions to compare them side by side.
              </p>
            ) : (
              <p className="text-sm text-zinc-600">
                {versions.length} versions saved. Compare any two, preview changes, or restore from the dialog.
              </p>
            )}
            <MuiButton
              variant="text"
              color="primary"
              size="small"
              startIcon={<CompareArrowsIcon fontSize="small" />}
              disabled={versions.length < 2}
              sx={{ alignSelf: "flex-start", fontWeight: 500 }}
              onClick={() => {
                const latest = versions[0];
                const older = versions[1];
                setComparePair({ v1: older.id, v2: latest.id });
              }}
            >
              Compare version
            </MuiButton>
          </div>
        )}
      </section>

      <VersionCompareDialog
        open={comparePair !== null}
        pageId={id}
        seedV1={comparePair?.v1 ?? ""}
        seedV2={comparePair?.v2 ?? ""}
        versions={versions}
        canEdit={canEdit}
        onClose={() => setComparePair(null)}
        onRollbackSuccess={() => void load()}
        onFetchError={(m) => setError(m)}
      />

      <section>
        <h2 className="mb-2 text-lg font-medium">Approvals ({history.length})</h2>
        <ApprovalPanel
          requests={history}
          pendingRequestId={pending?.id ?? null}
          canReview={canReview}
          reviewComment={reviewComment}
          onReviewCommentChange={setReviewComment}
          onApprove={async (rid, comment) => {
            await apiFetch(`/api/v1/approval-requests/${rid}/approve`, {
              method: "POST",
              json: { comment },
            });
            setReviewComment("");
            await load();
          }}
          onReject={async (rid, comment) => {
            await apiFetch(`/api/v1/approval-requests/${rid}/reject`, {
              method: "POST",
              json: { comment },
            });
            setReviewComment("");
            await load();
          }}
        />
      </section>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete page?"
        message={deletePageMessage}
        confirmLabel="Delete"
        danger
        onConfirm={() => void confirmDeletePage()}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
}
